import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAppContext } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Package,
  Download,
  ShoppingBag,
  Check,
  AlertCircle,
  Barcode,
  Image as ImageIcon,
  FileDown,
  Search
} from "lucide-react";
import { toast } from "sonner";
import Pagination from "../components/Pagination";

const PackagesPage = () => {
  const { packages: contextPackages, fetchPackages } = useAppContext();
  const [localPackages, setLocalPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    supplier_sku: "",
    description: "",
    length: "",
    width: "",
    height: "",
    weight: ""
  });

  // Sync local state with context
  useEffect(() => {
    if (contextPackages && contextPackages.length > 0) {
      setLocalPackages(contextPackages);
    }
  }, [contextPackages]);

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    setLoading(true);
    const data = await fetchPackages();
    if (data) setLocalPackages(data);
    setLoading(false);
  };

  // Use localPackages for filtering
  const packages = localPackages;

  // Filter packages based on search query
  const filteredPackages = packages.filter(pkg => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (pkg.name && pkg.name.toLowerCase().includes(query)) ||
      (pkg.sku && pkg.sku.toLowerCase().includes(query)) ||
      (pkg.description && pkg.description.toLowerCase().includes(query))
    );
  });

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const resetForm = () => {
    setFormData({
      name: "",
      sku: "",
      supplier_sku: "",
      description: "",
      length: "",
      width: "",
      height: "",
      weight: ""
    });
    setEditingPackage(null);
  };

  const handleOpenDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name,
        sku: pkg.sku || "",
        supplier_sku: pkg.supplier_sku || "",
        description: pkg.description || "",
        length: pkg.length?.toString() || "",
        width: pkg.width?.toString() || "",
        height: pkg.height?.toString() || "",
        weight: pkg.weight?.toString() || ""
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  // Sync from Shopify functions (Refresh)
  const handleOpenSyncDialog = async () => {
    setIsImportDialogOpen(true);
    setImportLoading(true);
    setImportPreview(null);
    
    try {
      const response = await axios.get(`${API}/packages/shopify-preview`);
      setImportPreview(response.data);
    } catch (error) {
      console.error("Failed to fetch Shopify preview:", error);
      // Set error state in preview
      setImportPreview({ success: false, message: error.response?.data?.detail || "Failed to connect to Shopify" });
    } finally {
      setImportLoading(false);
    }
  };

  const handleSync = async () => {
    setImporting(true);
    try {
      const response = await axios.post(`${API}/packages/sync-from-shopify`);
      
      const data = response.data;
      const summary = data.summary || {};
      
      // Build detailed success message
      let message = `Sync complete: `;
      const parts = [];
      if (summary.created > 0) parts.push(`${summary.created} new`);
      if (summary.updated > 0) parts.push(`${summary.updated} updated`);
      if (summary.unchanged > 0) parts.push(`${summary.unchanged} unchanged`);
      if (summary.deleted > 0) parts.push(`${summary.deleted} removed`);
      message += parts.join(', ') || 'No changes';
      
      toast.success(message);
      
      // Show info about preserved dimensions
      if (summary.updated > 0) {
        toast.info("Your custom dimensions have been preserved", { duration: 4000 });
      }
      
      if (data.errors && data.errors.length > 0) {
        data.errors.slice(0, 3).forEach(err => toast.error(err));
        if (data.errors.length > 3) {
          toast.error(`... and ${data.errors.length - 3} more errors`);
        }
      }
      
      setIsImportDialogOpen(false);
      await fetchPackages();
    } catch (error) {
      console.error("Sync failed:", error);
      toast.error(error.response?.data?.detail || "Failed to sync packages from Shopify");
    } finally {
      setImporting(false);
    }
  };

  // Legacy import function (keeping for backwards compatibility)
  const handleImport = async () => {
    // Redirect to sync
    await handleSync();
  };

  // Export to PDF
  const handleExportPDF = async () => {
    if (packages.length === 0) {
      toast.error("No packages to export");
      return;
    }
    
    setExporting(true);
    setExportProgress(0);
    
    // Show preparing toast
    const toastId = toast.loading("Preparing PDF export...", {
      description: "Compressing images and generating document"
    });
    
    try {
      const response = await axios.get(`${API}/packages/export/pdf`, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setExportProgress(progress);
            toast.loading(`Downloading PDF... ${progress}%`, {
              id: toastId,
              description: `${(progressEvent.loaded / 1024).toFixed(0)} KB of ${(progressEvent.total / 1024).toFixed(0)} KB`
            });
          }
        }
      });
      
      // Check file size
      const fileSize = response.data.size;
      if (fileSize > 6 * 1024 * 1024) {
        toast.error("PDF exceeds 6MB limit", { id: toastId });
        return;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `packages_export_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`PDF exported successfully (${(fileSize / 1024).toFixed(0)} KB)`, { id: toastId });
    } catch (error) {
      console.error("Export failed:", error);
      if (error.response?.status === 404) {
        toast.error("No packages to export", { id: toastId });
      } else if (error.response?.status === 413) {
        toast.error("PDF too large. Try reducing image quality.", { id: toastId });
      } else {
        toast.error("Failed to export PDF. Please try again.", { id: toastId });
      }
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error("Please enter a package name");
      return;
    }

    try {
      const payload = {
        name: formData.name,
        sku: formData.sku,
        supplier_sku: formData.supplier_sku,
        description: formData.description,
        length: formData.length ? parseFloat(formData.length) : null,
        width: formData.width ? parseFloat(formData.width) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null
      };

      if (editingPackage) {
        await axios.put(`${API}/packages/${editingPackage.id}`, payload);
        toast.success("Package updated successfully");
      } else {
        await axios.post(`${API}/packages`, payload);
        toast.success("Package created successfully");
      }

      // Close dialog and reset form
      setIsDialogOpen(false);
      resetForm();
      
      // Fetch fresh data and update local state directly
      const freshData = await axios.get(`${API}/packages`);
      setLocalPackages(freshData.data);
      
      // Also update context
      if (fetchPackages) fetchPackages();
    } catch (error) {
      console.error("Package save error:", error);
      toast.error(editingPackage ? "Failed to update package" : "Failed to create package");
    }
  };

  const handleDelete = async (pkgId) => {
    if (!confirm("Are you sure you want to delete this package?")) return;

    try {
      await axios.delete(`${API}/packages/${pkgId}`);
      toast.success("Package deleted");
      await fetchPackages();
    } catch (error) {
      toast.error("Failed to delete package");
    }
  };

  // Paginated packages (using filtered results)
  const paginatedPackages = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredPackages.slice(startIndex, startIndex + pageSize);
  }, [filteredPackages, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredPackages.length / pageSize);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F6F7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#16a34a]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            Package Presets
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage your package presets for quick waybill creation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleOpenSyncDialog}
            variant="outline"
            className="border-[#96BF48] text-[#96BF48] hover:bg-[#96BF48]/10"
            data-testid="import-shopify-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Refresh from Shopify
          </Button>
          
          <Button 
            onClick={handleExportPDF}
            variant="outline"
            className="border-slate-300 text-slate-600 hover:bg-slate-50 min-w-[140px]"
            disabled={exporting || packages.length === 0}
            data-testid="export-pdf-btn"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {exportProgress > 0 ? `${exportProgress}%` : 'Preparing...'}
              </>
            ) : (
              <>
                <FileDown className="w-4 h-4 mr-2" />
                Export PDF
              </>
            )}
          </Button>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-[#16a34a] hover:bg-[#15803d] text-white"
              data-testid="add-package-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Package
            </Button>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>{editingPackage ? "Edit Package" : "Add New Package"}</DialogTitle>
                <DialogDescription>
                  {editingPackage ? "Update package details" : "Create a package preset with SKU, dimensions and weight"}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Package Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Malfurn Classic Chair"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="package-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="sku">SKU</Label>
                    <Input
                      id="sku"
                      placeholder="e.g., MLFRN-CHR-001"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      data-testid="package-sku"
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    placeholder="Optional description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="length">Length (cm)</Label>
                    <Input
                      id="length"
                      type="number"
                      placeholder="120"
                      value={formData.length}
                      onChange={(e) => setFormData({ ...formData, length: e.target.value })}
                      data-testid="package-length"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="width">Width (cm)</Label>
                    <Input
                      id="width"
                      type="number"
                      placeholder="60"
                      value={formData.width}
                      onChange={(e) => setFormData({ ...formData, width: e.target.value })}
                      data-testid="package-width"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="height">Height (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      placeholder="80"
                      value={formData.height}
                      onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                      data-testid="package-height"
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="weight">Weight (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.1"
                    placeholder="25"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    data-testid="package-weight"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit}
                  className="bg-[#16a34a] hover:bg-[#15803d]"
                  data-testid="save-package-btn"
                >
                  {editingPackage ? "Update" : "Create"} Package
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Refresh from Shopify Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#96BF48]" />
              Refresh from Shopify
            </DialogTitle>
            <DialogDescription>
              Sync all active products from Shopify. Your custom dimensions are always preserved.
            </DialogDescription>
          </DialogHeader>
          
          {importLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#96BF48]" />
              <span className="ml-3 text-gray-600">Fetching products from Shopify...</span>
            </div>
          ) : importPreview && !importPreview.success ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">Shopify Not Connected</p>
              <p className="text-gray-500 text-center max-w-md mb-4">
                {importPreview.message || "Please connect your Shopify store in Settings before syncing products."}
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setIsImportDialogOpen(false);
                  window.location.href = '/settings';
                }}
                className="border-[#96BF48] text-[#96BF48] hover:bg-[#96BF48]/10"
              >
                Go to Settings
              </Button>
            </div>
          ) : importPreview && importPreview.success ? (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg mb-4">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-gray-500" />
                  <span className="font-medium">{importPreview.total_items}</span>
                  <span className="text-gray-500">SKUs in Shopify</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-green-600">{importPreview.new_items}</span>
                  <span className="text-gray-500">new</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-500" />
                  <span className="font-medium text-blue-600">{importPreview.existing_items}</span>
                  <span className="text-gray-500">will update</span>
                </div>
              </div>

              <div className="p-3 mb-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <strong>Dimensions are always preserved.</strong> Product names and images will sync from Shopify, but any dimensions (length, width, height) you've entered will be kept.
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-auto border rounded-lg">
                <Table>
                  <TableHeader className="sticky top-0 bg-white">
                    <TableRow>
                      <TableHead className="w-12">Image</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Variant</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Weight (kg)</TableHead>
                      <TableHead>Stock</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importPreview.items.map((item, idx) => (
                      <TableRow key={item.variant_id || item.product_title || `import-${idx}`} className={item.exists ? 'bg-blue-50/50' : ''}>
                        <TableCell>
                          {item.image_url ? (
                            <img src={item.image_url} alt="" className="w-8 h-8 object-contain rounded bg-white" />
                          ) : (
                            <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                              <ImageIcon className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{item.product_title}</TableCell>
                        <TableCell className="text-gray-600">{item.variant_title}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Barcode className="w-3 h-3 text-gray-400" />
                            <span className="font-mono text-sm">{item.sku}</span>
                          </div>
                        </TableCell>
                        <TableCell>{item.weight}</TableCell>
                        <TableCell>{item.inventory_quantity}</TableCell>
                        <TableCell>
                          {item.exists ? (
                            <Badge className="bg-blue-100 text-blue-800 border-blue-200">Update</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800 border-green-200">New</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-gray-500">
              No products found
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSync}
              disabled={importing || !importPreview || !importPreview.success || importPreview.total_items === 0}
              className="bg-[#96BF48] hover:bg-[#7da03a]"
              data-testid="confirm-import-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Sync {importPreview?.total_items || 0} SKUs
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Empty State */}
      {packages.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4">
              <Package className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">No packages yet</h3>
            <p className="text-sm text-slate-500 mb-4 text-center max-w-sm">
              Sync your Shopify SKUs or create custom package presets
            </p>
            <div className="flex gap-2">
              <Button 
                onClick={handleOpenSyncDialog}
                variant="outline"
                className="border-[#96BF48] text-[#96BF48] hover:bg-[#96BF48]/10"
              >
                <Download className="w-4 h-4 mr-2" />
                Refresh from Shopify
              </Button>
              <Button 
                onClick={() => handleOpenDialog()}
                className="bg-[#16a34a] hover:bg-[#15803d] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Package Manually
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Search Bar */}
          <Card className="border-slate-200 shadow-sm mb-4">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, SKU, or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-slate-200"
                  data-testid="package-search"
                />
              </div>
              {searchQuery && (
                <p className="text-sm text-slate-500 mt-2">
                  Found {filteredPackages.length} of {packages.length} packages
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              {/* Table List View - Read Only */}
              <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-16">Image</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead className="w-20">L (cm)</TableHead>
                  <TableHead className="w-20">W (cm)</TableHead>
                  <TableHead className="w-20">H (cm)</TableHead>
                  <TableHead className="w-24">Weight (kg)</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedPackages.map((pkg) => (
                  <TableRow key={pkg.id} className="hover:bg-slate-50">
                    <TableCell>
                      {pkg.image_url ? (
                        <img 
                          src={pkg.image_url} 
                          alt={pkg.name} 
                          className="w-10 h-10 object-contain rounded border border-gray-200 bg-white"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                          <Package className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{pkg.name}</TableCell>
                    <TableCell>
                      {pkg.sku ? (
                        <div className="flex items-center gap-1">
                          <Barcode className="w-3 h-3 text-gray-400" />
                          <span className="font-mono text-xs">{pkg.sku}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>{pkg.length || <span className="text-gray-400">-</span>}</TableCell>
                    <TableCell>{pkg.width || <span className="text-gray-400">-</span>}</TableCell>
                    <TableCell>{pkg.height || <span className="text-gray-400">-</span>}</TableCell>
                    <TableCell>{pkg.weight || <span className="text-gray-400">-</span>}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenDialog(pkg)}
                          className="h-7 w-7 p-0"
                        >
                          <Pencil className="w-3 h-3 text-gray-500" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(pkg.id)}
                          className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            {filteredPackages.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredPackages.length}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 25, 50, 100]}
              />
            )}
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default PackagesPage;
